import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Users, Camera, Instagram, ChevronLeft, ChevronRight, 
  ExternalLink, Loader2, Trophy, Star
} from 'lucide-react';
import api from '../../services/api';
import Footer from '../../components/Footer';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const OurPlayers = () => {
  const [heroImages, setHeroImages] = useState([]);
  const [instagramSettings, setInstagramSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactInfo, setContactInfo] = useState(null);
  const [businessLogo, setBusinessLogo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [imagesRes, instaRes, contactRes, logoRes] = await Promise.all([
        api.get('/hero-images?active_only=true'),
        api.get('/hero-images/instagram-feed'),
        fetch(`${API_URL}/api/contact-info`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/logo`).then(r => r.json()).catch(() => ({}))
      ]);
      setHeroImages(imagesRes.data || []);
      setInstagramSettings(instaRes.data || {});
      setContactInfo(contactRes);
      if (logoRes.logo_url) setBusinessLogo(logoRes.logo_url);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === heroImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? heroImages.length - 1 : prev - 1
    );
  };

  // Auto-advance carousel
  useEffect(() => {
    if (heroImages.length > 1) {
      const timer = setInterval(nextImage, 5000);
      return () => clearInterval(timer);
    }
  }, [heroImages.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              {businessLogo ? (
                <img 
                  src={`${API_URL}${businessLogo}`} 
                  alt={contactInfo?.business_name || "Cricket Academy"} 
                  className="h-12 w-auto" 
                />
              ) : (
                <span className="text-white font-bold text-lg">{contactInfo?.business_name || "Cricket Academy"}</span>
              )}
            </Link>
            <nav className="hidden md:flex items-center gap-4">
              <Link to="/" className="text-slate-300 hover:text-white transition-colors">Home</Link>
              <Link to="/our-players" className="text-white font-medium">Our Players</Link>
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => window.location.href = '/'}
              >
                Get Started
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section with Image Gallery */}
      <section className="relative py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our <span className="text-emerald-400">Players</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Capturing moments of excellence from our cricket academy
            </p>
          </div>

          {/* Image Gallery Carousel */}
          {heroImages.length > 0 ? (
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-[16/9] relative bg-slate-800">
                <img
                  src={heroImages[currentImageIndex]?.file_url}
                  alt={heroImages[currentImageIndex]?.title || 'Cricket action'}
                  className="w-full h-full object-cover"
                />
                
                {/* Image overlay with title */}
                {heroImages[currentImageIndex]?.title && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <h3 className="text-xl font-semibold text-white">
                      {heroImages[currentImageIndex].title}
                    </h3>
                    {heroImages[currentImageIndex].description && (
                      <p className="text-slate-300 text-sm mt-1">
                        {heroImages[currentImageIndex].description}
                      </p>
                    )}
                  </div>
                )}

                {/* Navigation arrows */}
                {heroImages.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Dots indicator */}
                {heroImages.length > 1 && (
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
                    {heroImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentImageIndex ? 'bg-emerald-400' : 'bg-white/50'
                        }`}
                        aria-label={`Go to image ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {heroImages.length > 1 && (
                <div className="flex gap-2 p-4 bg-slate-800/50 overflow-x-auto">
                  {heroImages.map((img, idx) => (
                    <button
                      key={img.id}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-emerald-400' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={img.file_url}
                        alt={img.title || `Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl bg-slate-800/50 border border-slate-700 p-12 text-center">
              <Camera className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-slate-400">Gallery Coming Soon</h3>
              <p className="text-slate-500 mt-2">Check back for photos from our latest matches and training sessions</p>
            </div>
          )}
        </div>
      </section>

      {/* Instagram Feed Section */}
      {instagramSettings?.enabled && instagramSettings?.handle && (
        <section className="py-12 px-4 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Follow Us on Instagram</h2>
                  <p className="text-slate-400">@{instagramSettings.handle}</p>
                </div>
              </div>
              <button
                onClick={() => window.open(`https://instagram.com/${instagramSettings.handle}`, '_blank', 'noopener,noreferrer')}
                className="hidden md:flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer"
              >
                <span>View Profile</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>

            {/* Instagram Profile Card */}
            <div className="bg-slate-800/50 rounded-xl p-8 text-center border border-slate-700">
              <Instagram className="w-12 h-12 text-pink-400 mx-auto mb-4" />
              <p className="text-slate-400 mb-4">
                Follow us on Instagram to see the latest updates from our academy!
              </p>
              
              {/* Instagram handle display */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg">
                  <Instagram className="w-5 h-5 text-pink-400" />
                  <span className="text-white font-medium">@{instagramSettings.handle}</span>
                </div>
                
                <a
                  href={`https://www.instagram.com/${instagramSettings.handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-full font-medium hover:opacity-90 transition-opacity"
                >
                  <ExternalLink className="w-4 h-4" />
                  Visit Profile
                </a>
              </div>
              
              <p className="text-slate-500 text-sm mt-4">
                instagram.com/{instagramSettings.handle}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 lg:p-6 text-center">
                <Users className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">200+</div>
                <div className="text-slate-400 text-sm">Active Players</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 lg:p-6 text-center">
                <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">50+</div>
                <div className="text-slate-400 text-sm">Tournaments</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 lg:p-6 text-center">
                <Star className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">15</div>
                <div className="text-slate-400 text-sm">Expert Coaches</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 lg:p-6 text-center">
                <Camera className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">10+</div>
                <div className="text-slate-400 text-sm">Years of Excellence</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-emerald-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Join Our Academy?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start your cricket journey with us today
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-slate-100">
              Get Started
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default OurPlayers;
